/**
 * Контракты режимов «Developer Pack» и «Email and Web Pack»
 * (ТЗ §6, режимы 11 и 12).
 *
 * Два режима, а механизм один: токены открываются группами, и следующая
 * группа ждёт, пока предыдущая не начнёт получаться. Различается материал —
 * скобки, слеши и операторы против собачки, точек и шаблонов адресов.
 *
 * От режимов символов эти два отличаются единицей: там тренируется клавиша,
 * здесь — устойчивое сочетание. `!=` набирается одним движением, и разложить
 * его на `!` и `=` значит потерять то, ради чего пакет существует.
 */

import type {
  HintLevel,
  LanguageCode,
  LayoutId,
  MasteryStat,
  MasteryUnit,
  TrainingMode,
} from '@type2x/shared-contracts';
import type { Finger } from '@type2x/shared-contracts';

/** Режимы, которые ведёт эта функция. */
export const PACK_MODES = ['developer-pack', 'email-web-pack'] as const;
export type PackMode = (typeof PACK_MODES)[number];

/**
 * Режимы функции — подмножество режимов обучения.
 *
 * Проверка на уровне типов: итог сессии уходит на сервер с этим значением, и
 * режим, которого нет в общем перечне, не был бы учтён в разнообразии.
 */
const MODES_ARE_TRAINING_MODES: readonly TrainingMode[] = PACK_MODES;
void MODES_ARE_TRAINING_MODES;

/**
 * Раскладка, на которой существует материал пакетов.
 *
 * Код и адреса пишутся латиницей, и ЙЦУКЕН физически не содержит ни `@`, ни
 * `[ ] { } < > ' \` & | $`: на ней эти режимы было бы нечем тренировать.
 * Прогресс копится отдельно для каждой раскладки (ТЗ §8.1), поэтому занятие
 * в латинской раскладке — не подмена профиля, а второй набор клавиш того же
 * профиля. Пользователю прямо говорится, что раскладку нужно переключить.
 */
export const PACK_LAYOUT: LayoutId = 'en-qwerty';

/** Снимок навыка, из которого считается открытость групп. */
export interface PacksSnapshot {
  readonly language: LanguageCode;
  readonly stats: Readonly<Record<string, MasteryStat>>;
}

/** Группа токенов одного режима. */
export interface PackGroup {
  readonly id: string;
  readonly mode: PackMode;
  /** Токены группы в порядке открытия. */
  readonly tokens: readonly string[];
}

/**
 * Фрагмент, внутри которого тренируется токен.
 *
 * Отдельный оператор посреди пустоты не тренирует ничего: `&&` живёт между
 * двумя условиями, а `@` — между именем и доменом. Поэтому элемент
 * упражнения — короткий фрагмент кода или адреса, а не сам символ.
 */
export interface PackFragment {
  readonly text: string;
  /** Токены пакета, встречающиеся во фрагменте. */
  readonly tokens: readonly string[];
}

/** Упражнение режима. */
export interface PacksExercise {
  readonly seed: string;
  /** Элементы: фрагменты кода либо адреса и ссылки. */
  readonly items: readonly string[];
  /** Текст для набора: элементы, соединённые пробелами. */
  readonly text: string;
}

/** Подсказка о Shift: какой символ и какую клавишу он меняет. */
export interface PackShiftHint {
  readonly character: string;
  /** Символ той же клавиши без Shift. */
  readonly base: string;
  readonly finger: Finger;
}

/** План занятия режима. */
export interface PacksPlan {
  readonly mode: PackMode;
  readonly language: LanguageCode;
  /** Раскладка занятия: всегда латинская, см. PACK_LAYOUT. */
  readonly layoutId: LayoutId;
  /** Группа, которую пользователь осваивает сейчас; null — открыты все. */
  readonly group: PackGroup | null;
  readonly completedGroupIds: readonly string[];
  /** Сколько всего групп в режиме: по нему считается доля пройденного. */
  readonly totalGroups: number;
  /** Сколько всего сочетаний в пакете: по нему видно, сколько уже открыто. */
  readonly totalTokens: number;
  /** Токены режима, открытые к этому моменту. */
  readonly unlockedTokens: readonly string[];
  /** Учебные единицы токенов, встречающихся в упражнении. */
  readonly units: readonly MasteryUnit[];
  readonly exercise: PacksExercise;
  /** Символы упражнения, которые требуют Shift. */
  readonly shiftHints: readonly PackShiftHint[];
  readonly hintLevel: HintLevel;
  /** Все группы пройдены: режиму больше нечему учить. */
  readonly finished: boolean;
}

/**
 * Сколько элементов в одном упражнении режима.
 *
 * Фрагмент кода короче адреса в два-три раза, поэтому их разное количество:
 * занятие должно быть одной длины, а не одного числа строк.
 */
export const ITEMS_PER_EXERCISE: Readonly<Record<PackMode, number>> = {
  'developer-pack': 10,
  'email-web-pack': 6,
};

/**
 * Приставка идентификаторов учебных единиц пакетов.
 *
 * Своя приставка, а не `key:`: клавиша `(` в режиме специальных символов и
 * пара `(x)` в коде — это разный навык, и складывать их статистику значило бы
 * считать освоенным то, что не проверялось.
 */
export const PACK_UNIT_PREFIX = 'pack:';

/**
 * Уровень подсказок режима.
 *
 * Клавиатура нужна обоим: символ, которого пользователь на латинской
 * раскладке ещё не искал, глазами по клавиатуре ищется всё равно (ТЗ §7.5).
 */
export const HINT_LEVEL: Readonly<Record<PackMode, HintLevel>> = {
  'developer-pack': 'full-keyboard-and-finger',
  'email-web-pack': 'full-keyboard-and-finger',
};
